import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { FocusableElementsUtil } from '@go-modules/utilities';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { RawEditorOptions } from 'hugerte';

interface MaxCharactersExceeded {
	maxExceeded: boolean;
	amountExceededBy: number;
}

@Component({
	selector: 'ngx-rich-text-editor',
	templateUrl: './rich-text-editor.component.html',
	styleUrls: ['./rich-text-editor.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => NgxRichTextEditorComponent),
			multi: true
		}
	],
	standalone: false
})
export class NgxRichTextEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
	@Input() public content: any = null;
	@Input() public placeholder: string;
	@Input() public autoSize: boolean = false;
	@Input() public containerClass: string = '';
	@Input() public autoFocus: boolean = false;
	@Input() public toolbarBelow: string = '';
	@Input() public toolbarOptions: string = 'bold italic underline strikethrough | alignleft aligncenter alignright | bullist numlist';
	@Input() public toolbarGroups: boolean = false;
	@Input() public showTopBorder: boolean = true;
	@Input() public hasRightButton: boolean = false;
	@Input() public label: string;
	@Input() public ariaLabel?: string;
	@Output() public enterEvent: EventEmitter<any> = new EventEmitter();
	@Output() public updateEvent: EventEmitter<string> = new EventEmitter();
	@Output() public onEditorFocusOut: EventEmitter<string> = new EventEmitter();
	@Output() public maxCharactersEvent: EventEmitter<MaxCharactersExceeded> = new EventEmitter();

	public hugerteOptions: RawEditorOptions;
	public editorContent: any = null;
	public onChangeFn: any;
	public onTouchedFn: any;
	public maxCharacters: number = clientSettings.CommentTextCharacterLimit;
	public currentEditor: any;
	public uniqueId = UniqueIdGeneratorUtil.getId();
	private isNormalizingToolbarRoles: boolean = false;
	private toolbarKeydownListener: ((event: KeyboardEvent) => void) | null = null;
	private toolbarFocusinListener: ((event: FocusEvent) => void) | null = null;
	private toolbarEntryTabStopObserver: MutationObserver | null = null;
	private toolbarGroupButtonMemory: WeakMap<HTMLElement, HTMLElement> = new WeakMap();
	private isProgrammaticallyFocusingToolbarGroup: boolean = false;
	private tooltipDismissKeydownListener: ((event: KeyboardEvent) => void) | null = null;
	private tooltipDismissKeyupListener: ((event: KeyboardEvent) => void) | null = null;
	private tooltipDismissDocument: Document | null = null;
	private tooltipDismissFocusTarget: HTMLElement | null = null;
	private shouldSuppressNextTooltipDismissKeyup: boolean = false;

	constructor (
		private translateService: TranslateService
	) {}

	public ngOnInit () {
		this.editorContent = this.content;
		const rightPadding = this.hasRightButton ? '40px' : '1px';
		this.hugerteOptions = {
			selector: `#${this.uniqueId} textarea`,
			browser_spellcheck: true,
			menubar: false,
			statusbar: false,
			height: 225,
			plugins: 'lists',
			toolbar: this.toolbarOptions,
			forced_root_block: 'div',
			object_resizing : false,
			placeholder: this.placeholder,
			entity_encoding: 'raw',
			// sanitizing removes inline styles so we need to preserve the following styles
			// in a different way (semantic or classes)
			formats: {
				underline: { inline: 'u', exact : true },
				alignleft: { block: 'p', classes: 'text-left' },
				aligncenter: { block: 'p', classes: 'text-center' },
				alignright: { block: 'p', classes: 'text-right' },
				strikethrough: { inline: 'del', exact: true }
			},
			content_style: `
				.mce-content-body { font-family: proxima-nova, "Open Sans", sans-serif; font-size: 14px; margin: 8px; min-height: 0; color: #333333; padding-right: `+rightPadding+` !important; }
				.mce-content-body img { border-radius: 10px; width: 100%; max-width: 450px; min-width: 268px; }
				.text-left { text-align: left; }
				.text-center { text-align: center; }
				.text-right { text-align: right; }
			`,
			valid_elements: 'b,strong,i,em,u,del,a[href|target=_blank],ul,ol,li,p[class],br,img[src|alt|width|height|style|class]',
			paste_as_text: false,
			paste_remove_styles: true,
			paste_webkit_styles: 'none',
			paste_preprocess: (_plugin, args) => {
				args.content = this.sanitizeHtmlContent(args.content);
			},
			iframe_aria_text: this.ariaLabel ?? this.translateService.instant('rich-text-editor_aria-label-with-help')
		} as RawEditorOptions;
		if (this.autoSize) {
			this.hugerteOptions.toolbar_location ='bottom';
			this.hugerteOptions.plugins = this.hugerteOptions.plugins + ' autoresize';
			this.hugerteOptions.height = 35;
			this.hugerteOptions.min_height = 35;
			if (this.containerClass) {
				const container = document.querySelector(`.${this.containerClass}`) as HTMLElement;
				const containerHeight = Math.max(35, container.offsetHeight);
				this.hugerteOptions.max_height = Math.min(271, (window.innerHeight * .28), containerHeight);
			} else {
				this.hugerteOptions.max_height = Math.min(271, (window.innerHeight * .28));
			}
			this.hugerteOptions.autoresize_bottom_margin = 0;
		}
		// TODO implement on upgrade to angular13 and tinymce5.2
		if (this.toolbarGroups) {
			this.hugerteOptions.toolbar_groups = {
				formatting: {
					icon: 'more-drawer',
					tooltip: 'More Options',
					items: 'bold italic underline strikethrough | aligncenter alignleft alignright bullist numlist'
				}
			};
		}
	}

	public ngOnDestroy (): void {
		this.detachToolbarKeydownListener();
		this.detachToolbarEntryTabStopObserver();
		this.detachTooltipDismissKeydownListener();
	}

	public blurHandler ($event) {
		if (this.editorContent) {
			this.updateEvent.emit(this.sanitizeEditorContent($event.editor));
			this.onChange($event);
		}
	}

	// focusout triggers when an element or *its children* lose focus (unlike blur)
	public focusOutHandler () {
		if (this.currentEditor == null) {
			return;
		}

		const editorContent = this.sanitizeEditorContent(this.currentEditor);
		this.onEditorFocusOut.emit(editorContent);
	}

	public focusHandler ($event) {
		if (this.editorContent) {
			$event.editor.setContent(this.editorContent);
			this.moveCursorToEnd($event.editor);
		}
	}

	public keyupHandler ($event) {
		if (this.handleTooltipDismissKeyup($event.event)) {
			return;
		}

		let node = $event.editor.selection.getNode();
		const nodeName = node.nodeName.toLowerCase();

		// styles applied to text are wrapped in span, this checks if span or styled text belongs under list element
		const isOnListNode = this.isRangeOnListNode(node);

		/*
			TODO: Remove these parts once we've updated tinymce to higher versions
			We should be able to have more options and control on how to alter line breaks behavior
		*/
		// to determine when to add/remove bullets, we check the range
		// (blinking focus " | ") on textarea if its on a list node
		if ($event.event.key === 'Enter' && $event.event.shiftKey && (nodeName === 'li' || isOnListNode)) {
			// tinymce sometimes adds <br> inside li element so we can't determine whether li contains values or not
			// this removes extraneous br content inside the li element
			node.querySelectorAll('br').forEach((element) => {
				element.remove();
			});
			node = $event.editor.selection.getNode();

			// check li content if empty or not
			// remove li if content is empty
			if (node.innerHTML.length > 0) {
				$event.editor.insertContent('<li></li>');

				// when adding/inserting new element, tinymce creates <br> with attr
				// [data-mce-bogus] when an added element is empty
				// this adds an extra list element with <br> into the list
				// instead of adding 1, we have 2 newly inserted li so we should remove the extra one
				/* eslint-disable-next-line max-len */
				// https://stackoverflow.com/questions/48882595/tinymce-br-data-mce-bogus-1-randomly-set-on-empty-textarea
				const bogusElem = $event.editor.getBody().querySelector('li [data-mce-bogus]');
				const listElements = $event.editor.getBody().querySelectorAll('li');
				const emptyElements = Array.from(listElements).find((li: HTMLElement) => {
					return li.childNodes.length === 0;
				});
				if (bogusElem && emptyElements) {
					const listElement = bogusElem.parentElement;
					const range = new Range();
					// since we'll be removing the bogus element, we should return cursor to previous sibling element
					range.setStart(listElement.previousElementSibling, 0);
					range.setEnd(listElement.previousElementSibling, 0);
					$event.editor.selection.setRng(range);
					listElement.remove();
				}
			} else {
				node.remove();
				$event.editor.setContent($event.editor.getContent() + '<p></p>');
				$event.editor.selection.select($event.editor.getBody().lastChild, true);
			}
		}

		this.updateEvent.emit(this.sanitizeEditorContent($event.editor));
		this.onChange($event);
	}

	public keydownHandler ($event) {
		if (this.handleTooltipDismissKeydown($event.event)) {
			return;
		}

		const nodeName = $event.editor.selection.getNode().nodeName.toLowerCase();

		if ($event.event.key === 'Tab' && $event.event.shiftKey) {
			if (this.focusLastToolbarTabStop($event.editor)) {
				$event.event.preventDefault();
				return;
			}
		}

		if ($event.event.key === 'Enter') {
			if (!$event.event.shiftKey) {
				if (nodeName !== 'li') {
					$event.editor.insertContent('<br>');
				}

				// when editing a comment tinymce is destroyed which can cause some errors
				// so allow it time to wrap everything it up first before destroying
				setTimeout(() => {
					this.onChange($event);
					this.enterEvent.emit($event);
				});
			} else if ($event.event.shiftKey && nodeName === 'li') {
				$event.event.preventDefault();
			}
		}
	}

	public initHandler ($event) {
		this.currentEditor = $event.editor;
		this.attachToolbarKeydownListener();
		this.attachToolbarEntryTabStopObserver();
		this.attachTooltipDismissKeydownListener();
		this.configureToolbarEntryTabStop();
		this.currentEditor.shortcuts.add('meta+alt+o', 'OrderedList', () => {
			this.currentEditor.execCommand('InsertOrderedList');
		});
		this.currentEditor.shortcuts.add('meta+alt+u', 'UnorderedList', () => {
			this.currentEditor.execCommand('InsertUnorderedList');
		});
		if (this.editorContent) {
			this.currentEditor.setContent(this.editorContent);
		}
		this.normalizeToolbarRoles(this.currentEditor);
		setTimeout(() => {
			this.normalizeToolbarRoles(this.currentEditor);
		});
		if (this.autoFocus) {
			this.focusOnField();
		}
	}

	public focusOnField () {
		setTimeout(() => {
			this.currentEditor.focus();
			// if content then do trickery to put cursor at end of content
			if (this.editorContent) {
				setTimeout(() => {
					this.currentEditor.focus();
					this.moveCursorToEnd(this.currentEditor);
				});
			}
		});
	}

	/**
	 * Implements ControlValueAccessor
	 */
	public onChange ($event) {
		const content = this.sanitizeEditorContent($event.editor);
		const textContent = $event.editor.getContent({format: 'text'});
		if (this.onChangeFn) {
			const valueToUse = content.length > 0 ? content : textContent;
			this.onChangeFn(valueToUse);
		}
		this.updateCharacterCount(textContent);
	}

	public updateCharacterCount (textContent) {
		if (textContent.length > this.maxCharacters) {
			this.maxCharactersEvent.emit({
				maxExceeded: true,
				amountExceededBy: textContent.length - this.maxCharacters
			});
		} else {
			this.maxCharactersEvent.emit({
				maxExceeded: false,
				amountExceededBy: 0
			});
		}
	}

	public onTouched () {
		if (this.onTouchedFn) this.onTouchedFn();
	}

	public registerOnChange (onChange: any): void {
		this.onChangeFn = onChange;
	}

	public registerOnTouched (onTouched: any): void {
		this.onTouchedFn = onTouched;
	}

	public writeValue (text: string): void {
		if (this.currentEditor) {
			this.currentEditor.setContent(text);
		}
		this.editorContent = text;
	}

	private isRangeOnListNode (node) {
		let isOnListNode = false;
		while(node.parentNode) {
			node = node.parentNode;
			if (node.nodeName.toLowerCase() === 'li') {
				isOnListNode = true;
				break;
			}
		}
		return isOnListNode;
	}

	private moveCursorToEnd (editor): void {
		editor.selection.select(editor.getBody(), true);
		editor.selection.collapse(false);
	}

	private focusLastToolbarTabStop (editor): boolean {
		const toolbarContainer = editor?.editorContainer?.querySelector('.tox-editor-header, .mce-toolbar-grp');
		if (!(toolbarContainer instanceof HTMLElement)) {
			return false;
		}

		const toolbarTabStops = this.getAlloyToolbarTabStopElements(toolbarContainer);
		const lastToolbarTabStop = toolbarTabStops.at(-1);
		if (lastToolbarTabStop != null) {
			this.focusToolbarGroup(lastToolbarTabStop);
			return true;
		}

		return false;
	}

	private attachToolbarKeydownListener (): void {
		this.detachToolbarKeydownListener();
		const toolbarContainer = this.getToolbarContainer();
		if (toolbarContainer == null) {
			return;
		}

		this.toolbarKeydownListener = (event: KeyboardEvent) => {
			this.handleToolbarKeydown(event);
		};
		this.toolbarFocusinListener = (event: FocusEvent) => {
			this.handleToolbarFocusin(event);
		};

		toolbarContainer.addEventListener('keydown', this.toolbarKeydownListener, true);
		toolbarContainer.addEventListener('focusin', this.toolbarFocusinListener, true);
	}

	private detachToolbarKeydownListener (): void {
		const toolbarContainer = this.getToolbarContainer();
		if (toolbarContainer != null && this.toolbarKeydownListener != null) {
			toolbarContainer.removeEventListener('keydown', this.toolbarKeydownListener, true);
		}
		if (toolbarContainer != null && this.toolbarFocusinListener != null) {
			toolbarContainer.removeEventListener('focusin', this.toolbarFocusinListener, true);
		}

		this.toolbarKeydownListener = null;
		this.toolbarFocusinListener = null;
	}

	private getToolbarContainer (): HTMLElement | null {
		const toolbarContainer = this.currentEditor?.editorContainer?.querySelector('.tox-editor-header, .mce-toolbar-grp');
		return toolbarContainer instanceof HTMLElement ? toolbarContainer : null;
	}

	private getToolbarFocusableElements (toolbarContainer: HTMLElement): HTMLElement[] {
		return FocusableElementsUtil.getAllFrom(toolbarContainer);
	}

	private getAlloyToolbarTabStopElements (toolbarContainer: HTMLElement): HTMLElement[] {
		return Array.from(toolbarContainer.querySelectorAll('[data-alloy-tabstop="true"]'))
			.filter((element): element is HTMLElement => element instanceof HTMLElement);
	}

	private getToolbarTabStopElements (toolbarContainer: HTMLElement): HTMLElement[] {
		const toolbarTabStops = this.getAlloyToolbarTabStopElements(toolbarContainer);
		if (toolbarTabStops.length > 0) {
			return toolbarTabStops;
		}

		return this.getToolbarFocusableElements(toolbarContainer);
	}

	private getToolbarButtons (container: HTMLElement): HTMLElement[] {
		return Array.from(container.querySelectorAll('.tox-tbtn'))
			.filter((element): element is HTMLElement => element instanceof HTMLElement);
	}

	private getToolbarButtonTarget (eventTarget: HTMLElement): HTMLElement | null {
		return eventTarget.closest('.tox-tbtn') as HTMLElement | null;
	}

	private configureToolbarEntryTabStop (): void {
		const toolbarContainer = this.getToolbarContainer();
		if (toolbarContainer == null) {
			return;
		}

		const toolbarTabStops = this.getAlloyToolbarTabStopElements(toolbarContainer);
		if (toolbarTabStops.length === 0) {
			return;
		}

		toolbarTabStops.forEach((toolbarTabStop, index) => {
			const tabIndex = index === 0 ? 0 : -1;
			if (toolbarTabStop.tabIndex !== tabIndex) {
				toolbarTabStop.tabIndex = tabIndex;
			}
		});
	}

	private getToolbarGroupFocusTarget (toolbarGroup: HTMLElement): HTMLElement | null {
		const toolbarButtons = this.getToolbarButtons(toolbarGroup);
		const rememberedToolbarButton = this.toolbarGroupButtonMemory.get(toolbarGroup);
		return rememberedToolbarButton != null && toolbarGroup.contains(rememberedToolbarButton)
			? rememberedToolbarButton
			: toolbarButtons[0];
	}

	private getFirstToolbarGroupFocusTarget (toolbarGroup: HTMLElement): HTMLElement | null {
		return this.getToolbarButtons(toolbarGroup)[0];
	}

	private focusToolbarGroup (toolbarGroup: HTMLElement): void {
		this.isProgrammaticallyFocusingToolbarGroup = true;
		try {
			toolbarGroup.focus();
		} finally {
			this.isProgrammaticallyFocusingToolbarGroup = false;
		}

		const targetButton = this.getToolbarGroupFocusTarget(toolbarGroup);
		if (toolbarGroup.ownerDocument.activeElement === toolbarGroup && targetButton != null) {
			targetButton.focus();
		}
	}

	private handleToolbarFocusin (event: FocusEvent): void {
		const toolbarContainer = this.getToolbarContainer();
		if (toolbarContainer == null || !(event.target instanceof HTMLElement)) {
			return;
		}
		this.normalizeToolbarRoles(this.currentEditor);

		const toolbarButton = this.getToolbarButtonTarget(event.target);
		if (toolbarButton instanceof HTMLElement) {
			const toolbarGroup = toolbarButton.closest('[data-alloy-tabstop="true"]');
			if (toolbarGroup instanceof HTMLElement && toolbarContainer.contains(toolbarGroup)) {
				this.toolbarGroupButtonMemory.set(toolbarGroup, toolbarButton);
			}
			return;
		}

		if (this.isToolbarGroupTabStop(event.target)) {
			if (this.isProgrammaticallyFocusingToolbarGroup) {
				return;
			}

			const targetButton = this.getFirstToolbarGroupFocusTarget(event.target);
			if (targetButton != null && event.target.ownerDocument.activeElement === event.target) {
				targetButton.focus();
			}
		}
	}

	private handleToolbarKeydown (event: KeyboardEvent): void {
		if (event.key !== 'Tab') {
			return;
		}

		const toolbarContainer = this.getToolbarContainer();
		const alloyToolbarGroups = toolbarContainer == null
			? []
			: this.getAlloyToolbarTabStopElements(toolbarContainer);
		const currentButtonTarget = event.target instanceof HTMLElement
			? this.getToolbarButtonTarget(event.target)
			: null;
		if (alloyToolbarGroups.length > 0) {
			const currentToolbarGroup = currentButtonTarget instanceof HTMLElement
				? currentButtonTarget.closest('[data-alloy-tabstop="true"]')
				: event.target instanceof HTMLElement
					? this.getToolbarCurrentTarget(event.target)
					: null;
			if (
				currentToolbarGroup instanceof HTMLElement
				&& toolbarContainer?.contains(currentToolbarGroup)
			) {
				this.handleAlloyToolbarGroupKeydown(event, alloyToolbarGroups, currentToolbarGroup);
			}
			return;
		}

		if (currentButtonTarget instanceof HTMLElement && toolbarContainer?.contains(currentButtonTarget)) {
			this.handleToolbarButtonKeydown(event, toolbarContainer, currentButtonTarget);
			return;
		}

		const currentTarget = event.target instanceof HTMLElement
			? this.getToolbarCurrentTarget(event.target)
			: null;

		if (
			!(currentTarget instanceof HTMLElement)
			|| toolbarContainer == null
			|| !toolbarContainer.contains(currentTarget)
		) {
			return;
		}

		const toolbarTabStops = this.getToolbarTabStopElements(toolbarContainer);
		const currentIndex = toolbarTabStops.indexOf(currentTarget);
		if (currentIndex === -1) {
			return;
		}

		event.preventDefault();
		if (event.shiftKey) {
			if (currentIndex === 0) {
				this.focusPreviousElementOutsideEditor();
				return;
			}

			toolbarTabStops[currentIndex - 1].focus();
			return;
		}

		if (currentIndex < toolbarTabStops.length - 1) {
			toolbarTabStops[currentIndex + 1].focus();
			return;
		}

		this.focusAfterLastToolbarControl();
	}

	private handleAlloyToolbarGroupKeydown (
		event: KeyboardEvent,
		toolbarGroups: HTMLElement[],
		currentToolbarGroup: HTMLElement
	): void {
		const currentIndex = toolbarGroups.indexOf(currentToolbarGroup);
		if (currentIndex === -1) {
			return;
		}

		if (event.shiftKey) {
			if (currentIndex === 0) {
				event.preventDefault();
				this.focusPreviousElementOutsideEditor();
			}
			return;
		}

		if (currentIndex === toolbarGroups.length - 1) {
			event.preventDefault();
			this.focusAfterLastToolbarControl();
		}
	}

	private handleToolbarButtonKeydown (
		event: KeyboardEvent,
		toolbarContainer: HTMLElement,
		currentButtonTarget: HTMLElement
	): void {
		const toolbarButtons = this.getToolbarButtons(toolbarContainer);
		const currentIndex = toolbarButtons.indexOf(currentButtonTarget);
		if (currentIndex === -1) {
			return;
		}

		event.preventDefault();
		if (event.shiftKey) {
			if (currentIndex === 0) {
				this.focusPreviousElementOutsideEditor();
				return;
			}

			toolbarButtons[currentIndex - 1].focus();
			return;
		}

		if (currentIndex < toolbarButtons.length - 1) {
			toolbarButtons[currentIndex + 1].focus();
			return;
		}

		this.focusAfterLastToolbarControl();
	}

	private attachToolbarEntryTabStopObserver (): void {
		this.detachToolbarEntryTabStopObserver();
		if (typeof MutationObserver === 'undefined') {
			return;
		}

		const editorContainer = this.currentEditor?.editorContainer;
		if (!(editorContainer instanceof HTMLElement)) {
			return;
		}

		this.toolbarEntryTabStopObserver = new MutationObserver(() => {
			this.configureToolbarEntryTabStop();
			this.normalizeToolbarRoles(this.currentEditor);
		});
		this.toolbarEntryTabStopObserver.observe(editorContainer, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['tabindex']
		});
	}

	private detachToolbarEntryTabStopObserver (): void {
		this.toolbarEntryTabStopObserver?.disconnect();
		this.toolbarEntryTabStopObserver = null;
	}

	private attachTooltipDismissKeydownListener (): void {
		this.detachTooltipDismissKeydownListener();

		const editorContainer = this.currentEditor?.editorContainer;
		if (!(editorContainer instanceof HTMLElement)) {
			return;
		}

		this.tooltipDismissKeydownListener = (event: KeyboardEvent) => {
			this.handleTooltipDismissKeydown(event);
		};
		this.tooltipDismissKeyupListener = (event: KeyboardEvent) => {
			this.handleTooltipDismissKeyup(event);
		};
		this.tooltipDismissDocument = editorContainer.ownerDocument;
		this.tooltipDismissDocument.addEventListener('keydown', this.tooltipDismissKeydownListener, {
			capture: true,
			passive: false
		});
		this.tooltipDismissDocument.addEventListener('keyup', this.tooltipDismissKeyupListener, {
			capture: true,
			passive: false
		});
	}

	private detachTooltipDismissKeydownListener (): void {
		if (this.tooltipDismissDocument != null) {
			if (this.tooltipDismissKeydownListener != null) {
				this.tooltipDismissDocument.removeEventListener('keydown', this.tooltipDismissKeydownListener, {
					capture: true
				});
			}
			if (this.tooltipDismissKeyupListener != null) {
				this.tooltipDismissDocument.removeEventListener('keyup', this.tooltipDismissKeyupListener, {
					capture: true
				});
			}
		}

		this.tooltipDismissDocument = null;
		this.tooltipDismissKeydownListener = null;
		this.tooltipDismissKeyupListener = null;
		this.tooltipDismissFocusTarget = null;
		this.shouldSuppressNextTooltipDismissKeyup = false;
	}

	private handleTooltipDismissKeydown (event: KeyboardEvent): boolean {
		if (event.key !== 'Escape') {
			return false;
		}

		const focusTarget = this.getTooltipDismissFocusTarget(event);
		if (!this.dismissEditorTooltips()) {
			return false;
		}

		this.tooltipDismissFocusTarget = focusTarget;
		this.shouldSuppressNextTooltipDismissKeyup = true;
		event.stopImmediatePropagation();
		this.preserveTooltipDismissFocus(focusTarget);
		return true;
	}

	private handleTooltipDismissKeyup (event: KeyboardEvent): boolean {
		if (
			event.key !== 'Escape'
			|| !this.shouldSuppressNextTooltipDismissKeyup
		) {
			return false;
		}

		event.stopImmediatePropagation();
		this.preserveTooltipDismissFocus(this.tooltipDismissFocusTarget);
		this.tooltipDismissFocusTarget = null;
		this.shouldSuppressNextTooltipDismissKeyup = false;
		return true;
	}

	private getTooltipDismissFocusTarget (event: KeyboardEvent): HTMLElement | null {
		const toolbarContainer = this.getToolbarContainer();
		if (toolbarContainer == null) {
			return null;
		}

		const eventTarget = event.target instanceof HTMLElement ? event.target : null;
		const activeElement = eventTarget?.ownerDocument.activeElement;
		if (activeElement instanceof HTMLElement && toolbarContainer.contains(activeElement)) {
			return activeElement;
		}

		if (eventTarget instanceof HTMLElement && toolbarContainer.contains(eventTarget)) {
			return eventTarget;
		}

		return null;
	}

	private preserveTooltipDismissFocus (focusTarget: HTMLElement | null): void {
		if (focusTarget == null || !focusTarget.isConnected) {
			return;
		}

		// Preserve the toolbar focus target after Escape dismisses its tooltip.
		setTimeout(() => {
			const toolbarContainer = this.getToolbarContainer();
			if (
				focusTarget.isConnected
				&& toolbarContainer != null
				&& toolbarContainer.contains(focusTarget)
				&& focusTarget.ownerDocument.activeElement !== focusTarget
			) {
				focusTarget.focus();
			}
		});
	}

	private dismissEditorTooltips (): boolean {
		const editorContainer = this.currentEditor?.editorContainer;
		if (!(editorContainer instanceof HTMLElement)) {
			return false;
		}

		const tooltipElements = Array.from(
			editorContainer.ownerDocument.querySelectorAll('.tox.tox-hugerte-aux .tox-tooltip')
		);
		if (tooltipElements.length === 0) {
			return false;
		}

		tooltipElements.forEach((tooltipElement) => {
			tooltipElement.remove();
		});

		return true;
	}

	private focusAfterLastToolbarControl (): void {
		if (this.isToolbarBelowEditor() && this.focusNextElementOutsideEditor()) {
			return;
		}

		this.currentEditor?.focus();
	}

	private isToolbarBelowEditor (): boolean {
		return this.autoSize || this.toolbarBelow !== '';
	}

	private focusNextElementOutsideEditor (): boolean {
		const editorContainer = this.currentEditor?.editorContainer;
		if (!(editorContainer instanceof HTMLElement)) {
			return false;
		}

		const focusableElements = FocusableElementsUtil.getAllFrom(editorContainer.ownerDocument.body);
		const nextElement = focusableElements.find((element) => {
			const position = editorContainer.compareDocumentPosition(element);
			const isAfterEditor =
				position === Node.DOCUMENT_POSITION_FOLLOWING
				|| position === Node.DOCUMENT_POSITION_FOLLOWING + Node.DOCUMENT_POSITION_IMPLEMENTATION_SPECIFIC;
			return !editorContainer.contains(element) && isAfterEditor;
		});

		if (nextElement == null) {
			return false;
		}

		nextElement.focus();
		return true;
	}

	private focusPreviousElementOutsideEditor (): void {
		const editorContainer = this.currentEditor?.editorContainer;
		if (!(editorContainer instanceof HTMLElement)) {
			return;
		}

		const focusableElements = FocusableElementsUtil.getAllFrom(editorContainer.ownerDocument.body);
		const previousElement = [...focusableElements].reverse().find((element) => {
			const position = element.compareDocumentPosition(editorContainer);
			const isBeforeEditor =
				position === Node.DOCUMENT_POSITION_FOLLOWING
				|| position === Node.DOCUMENT_POSITION_FOLLOWING + Node.DOCUMENT_POSITION_IMPLEMENTATION_SPECIFIC;

			return !editorContainer.contains(element) && isBeforeEditor;
		});

		previousElement?.focus();
	}

	private getToolbarCurrentTarget (eventTarget: HTMLElement): HTMLElement | null {
		const toolbarGroup = eventTarget.closest('[data-alloy-tabstop="true"]');
		if (toolbarGroup instanceof HTMLElement) {
			return toolbarGroup;
		}

		return eventTarget.closest('button, [tabindex]:not([tabindex="-1"])') as HTMLElement | null;
	}

	private isToolbarGroupTabStop (element: HTMLElement): boolean {
		return element.matches('[data-alloy-tabstop="true"]');
	}

	private normalizeToolbarRoles (editor): void {
		if (this.isNormalizingToolbarRoles) {
			return;
		}

		this.isNormalizingToolbarRoles = true;
		try {
			const editorContainer: HTMLElement | null = editor?.editorContainer ?? null;
			if (editorContainer == null) {
				return;
			}

			const mainToolbar: HTMLElement | null = editorContainer.querySelector('.tox-toolbar__primary, .tox-toolbar');
			if (mainToolbar != null && mainToolbar.getAttribute('role') !== 'toolbar') {
				mainToolbar.setAttribute('role', 'toolbar');
			}

			editorContainer.querySelectorAll('.tox-toolbar__group').forEach((groupElement) => {
				groupElement.removeAttribute('role');
			});

			editorContainer.querySelectorAll('[role="toolbar"]').forEach((toolbarElement) => {
				if (toolbarElement !== mainToolbar) {
					toolbarElement.removeAttribute('role');
				}
			});

			editorContainer.querySelectorAll('[role="group"]').forEach((groupElement) => {
				groupElement.removeAttribute('role');
			});
		} finally {
			this.isNormalizingToolbarRoles = false;
		}
	}

	private sanitizeHtmlContent (content: string): string {
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = content;

		this.removeCommentNodes(tempDiv);
		this.removeBase64Images(tempDiv);

		return tempDiv.innerHTML.trim();
	}

	private sanitizeEditorContent (editor): string {
		const content = editor.getContent();
		return this.sanitizeHtmlContent(content);
	}

	private removeCommentNodes (container: HTMLElement): void {
		const commentNodes: Comment[] = [];
		const treeWalker = document.createTreeWalker(container, NodeFilter.SHOW_COMMENT);
		let currentNode = treeWalker.nextNode();

		while (currentNode != null) {
			commentNodes.push(currentNode as Comment);
			currentNode = treeWalker.nextNode();
		}

		commentNodes.forEach((commentNode) => {
			commentNode.remove();
		});
	}

	private removeBase64Images (container: HTMLElement): void {
		container.querySelectorAll('img').forEach((img) => {
			if (img.src.startsWith('data:image')) {
				img.remove();
			}
		});
	}
}
